import supabase from '../lib/supabaseClient';

/**
 * Admin Authentication Service
 * Handles admin login, logout, and session management
 */

/**
 * Login admin with email and password
 * @param {string} email - Admin email
 * @param {string} password - Admin password
 * @returns {Promise<{success: boolean, admin?: object, error?: string}>}
 */
export const loginAdmin = async (email, password) => {
  try {
    // Step 1: Authenticate with Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (authError) {
      return { success: false, error: authError.message };
    }

    // Step 2: Verify user is an admin
    const { data: adminData, error: adminError } = await supabase
      .from('admin')
      .select('*')
      .eq('email', email)
      .eq('is_active', true)
      .single();

    if (adminError || !adminData) {
      // User authenticated but not an admin - sign them out
      await supabase.auth.signOut();
      return { success: false, error: 'Unauthorized: Admin access required' };
    }

    // Step 3: Store admin session
    const adminSession = {
      id: adminData.id,
      email: adminData.email,
      username: adminData.username,
      role: adminData.role,
      loginTime: new Date().toISOString()
    };

    localStorage.setItem('adminAuth', JSON.stringify(adminSession));

    return { success: true, admin: adminSession };
  } catch (error) {
    return { success: false, error: 'Login failed. Please try again.' };
  }
};

/**
 * Logout admin
 */
export const logoutAdmin = async () => {
  try {
    // Sign out from Supabase
    await supabase.auth.signOut();

    // Clear local storage
    localStorage.removeItem('adminAuth');

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Check if admin is authenticated
 * @returns {boolean}
 */
export const isAdminAuthenticated = () => {
  const adminAuth = localStorage.getItem('adminAuth');

  if (!adminAuth) {
    return false;
  }

  try {
    const authData = JSON.parse(adminAuth);

    // Check session expiration (24 hours)
    const loginTime = new Date(authData.loginTime);
    const now = new Date();
    const hoursSinceLogin = (now - loginTime) / (1000 * 60 * 60);

    if (hoursSinceLogin > 24) {
      localStorage.removeItem('adminAuth');
      return false;
    }

    // Check if user has admin role (super_admin, admin, or moderator)
    return ['super_admin', 'admin', 'moderator'].includes(authData.role);
  } catch (error) {
    localStorage.removeItem('adminAuth');
    return false;
  }
};

/**
 * Get current admin data
 * @returns {object|null}
 */
export const getCurrentAdmin = () => {
  const adminAuth = localStorage.getItem('adminAuth');

  if (!adminAuth) {
    return null;
  }

  try {
    return JSON.parse(adminAuth);
  } catch (error) {
    return null;
  }
};

/**
 * Create a new admin user (must be called by existing admin or via backend)
 * @param {object} adminData - Admin user data
 * @returns {Promise<{success: boolean, admin?: object, error?: string}>}
 */
export const createAdminUser = async (adminData) => {
  try {
    const { data, error } = await supabase
      .from('admin')
      .insert([{
        username: adminData.username,
        email: adminData.email,
        phone: adminData.phone,
        role: adminData.role || 'admin',
        is_active: true
      }])
      .select()
      .single();

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, admin: data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

const adminAuthService = {
  loginAdmin,
  logoutAdmin,
  isAdminAuthenticated,
  getCurrentAdmin,
  createAdminUser
};

export default adminAuthService;
